import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { extractErrorMessage } from '@/shared/api/errors';
import { usePackageDetail } from '@/entities/packages';
import { WidgetsPackageFormMain } from '@/widgets/package-form';

export const PagesPackageEditMain = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const query = usePackageDetail(id);

  if (query.isLoading) {
    return (
      <>
        <PageHeader title={t('packages.form.editTitle')} showBack backTo="/packages" />
        <div className="space-y-3 px-4 py-4 lg:px-6 lg:py-6">
          <div className="h-10 animate-pulse rounded-lg bg-slate-200/70" />
          <div className="h-64 animate-pulse rounded-lg bg-slate-200/60" />
          <div className="flex items-center justify-center pt-4">
            <Spinner size="md" />
          </div>
        </div>
      </>
    );
  }

  const status =
    query.error instanceof AxiosError ? query.error.response?.status : undefined;
  if (status === 404) {
    return (
      <>
        <PageHeader title={t('packages.form.editTitle')} showBack backTo="/packages" />
        <div className="space-y-3 px-4 py-6 lg:px-6 lg:py-8">
          <p className="text-sm text-ink-soft">{t('packages.form.notFound')}</p>
          <Link
            to="/packages"
            className="text-sm font-medium text-brand-600 hover:underline"
          >
            {t('common.cancel')}
          </Link>
        </div>
      </>
    );
  }

  if (query.error || !query.data) {
    return (
      <>
        <PageHeader title={t('packages.form.editTitle')} showBack backTo="/packages" />
        <div className="px-4 py-4 lg:px-6 lg:py-6">
          <ErrorState
            message={extractErrorMessage(query.error, t('packages.form.loadError'))}
            onRetry={() => void query.refetch()}
          />
        </div>
      </>
    );
  }

  return <WidgetsPackageFormMain mode="edit" pkg={query.data} />;
};
